import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@antimatterhq/ui/lib/utils';
import { useColorMode } from '@antimatterhq/ui/contexts/ColorModeContext';
import { Textarea } from '../textarea/textarea';

const highlightTextareaVariants = cva(
  [
    'flex w-full rounded-md border px-3 py-2 text-sm relative',
    'bg-white dark:bg-transparent',
    'placeholder:text-grey-400 dark:placeholder:text-grey-500',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        default: [
          'border-grey-300 dark:border-grey-650',
          'text-grey-800 dark:text-grey-100',
          'focus-visible:ring-blue-600 dark:focus-visible:ring-blue-500',
        ],
        success: [
          'border-green-600 dark:border-green-500',
          'text-green-600 dark:text-green-500',
          'focus-visible:ring-green-600 dark:focus-visible:ring-green-500',
        ],
        warning: [
          'border-yellow-600 dark:border-yellow-500',
          'text-yellow-600 dark:text-yellow-500',
          'focus-visible:ring-yellow-600 dark:focus-visible:ring-yellow-500',
        ],
        error: [
          'border-red-600 dark:border-red-500',
          'text-red-600 dark:text-red-500',
          'focus-visible:ring-red-600 dark:focus-visible:ring-red-500',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface Highlight {
  start: number;
  end: number;
  variant: 'success' | 'warning' | 'error' | 'primary';
}

export interface HighlightTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'>,
    VariantProps<typeof highlightTextareaVariants> {
  value: string;
  highlights?: Highlight[];
  height?: string;
}

const HighlightTextarea = React.forwardRef<
  HTMLTextAreaElement,
  HighlightTextareaProps
>(
  (
    { className, variant, value, highlights = [], height = '80px', ...props },
    ref,
  ) => {
    // Create a container for both the textarea and the highlighting layer
    const containerRef = React.useRef<HTMLDivElement>(null);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const highlightLayerRef = React.useRef<HTMLDivElement>(null);

    const { isDarkMode } = useColorMode();

    React.useImperativeHandle(ref, () => textareaRef.current!);

    // Sync scroll position between textarea and highlight layer
    const handleScroll = React.useCallback(() => {
      if (textareaRef.current && highlightLayerRef.current) {
        highlightLayerRef.current.scrollTop = textareaRef.current.scrollTop;
        highlightLayerRef.current.scrollLeft = textareaRef.current.scrollLeft;
      }
    }, []);

    React.useEffect(() => {
      handleScroll();
    }, [value, handleScroll]);

    const getHighlightColor = (highlightVariant: Highlight['variant']) => {
      switch (highlightVariant) {
        case 'success':
          return 'bg-green-400 dark:bg-green-600';
        case 'warning':
          return 'bg-yellow-400 dark:bg-yellow-600';
        case 'error':
          return 'bg-red-400 dark:bg-red-600';
        case 'primary':
          return 'bg-blue-400 dark:bg-blue-600';
        default:
          return 'transparent';
      }
    };

    // Create highlighted content
    const renderHighlights = React.useCallback(() => {
      const result = value;
      const spans: JSX.Element[] = [];
      let lastIndex = 0;

      // Sort highlights by start position to handle overlaps
      const sortedHighlights = [...highlights].sort(
        (a, b) => a.start - b.start,
      );

      sortedHighlights.forEach((highlight, index) => {
        if (highlight.start > lastIndex) {
          // Add non-highlighted text before this highlight
          spans.push(
            <span key={`text-${index}`}>
              {result.substring(lastIndex, highlight.start)}
            </span>,
          );
        }

        // Add highlighted text
        spans.push(
          <span
            key={`highlight-${index}`}
            className={getHighlightColor(highlight.variant)}
          >
            {result.substring(highlight.start, highlight.end)}
          </span>,
        );

        lastIndex = highlight.end;
      });

      // Add any remaining text
      if (lastIndex < result.length) {
        spans.push(<span key="text-end">{result.substring(lastIndex)}</span>);
      }

      // Add empty div if the last character is a newline
      if (result.length > 0 && result[result.length - 1] === '\n') {
        spans.push(<span key="empty-line">&nbsp;</span>);
      }

      return spans;
    }, [value, highlights]);

    return (
      <div
        ref={containerRef}
        className={cn(highlightTextareaVariants({ variant }), className)}
        style={{ height }}
      >
        {/* Highlighting layer */}
        <div
          ref={highlightLayerRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 box-border overflow-auto whitespace-pre leading-[1.2]"
          style={{
            padding: '8px 5px 8px 12px',
            userSelect: 'none',
            scrollbarColor: 'transparent transparent',
            msOverflowStyle: 'none',
            border: '1px solid transparent',
          }}
        >
          {renderHighlights()}
        </div>

        {/* Actual textarea */}
        <Textarea
          ref={textareaRef}
          value={value}
          onScroll={handleScroll}
          className="absolute inset-0 h-full w-full resize-none overflow-auto whitespace-pre bg-transparent leading-[1.2]"
          style={{
            padding: '8px 12px',
            caretColor: isDarkMode ? 'white' : 'black',
            color: 'rgba(0, 0, 0, 0)',
            WebkitTextFillColor: 'transparent',
          }}
          {...props}
        />
      </div>
    );
  },
);

HighlightTextarea.displayName = 'HighlightTextarea';

export { HighlightTextarea };
